import { useState, useCallback, useRef, useEffect } from 'react';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {
  faGear,
  faTimes,
  faGripVertical,
  faPen,
  faExpand,
  faBell,
} from '@fortawesome/free-solid-svg-icons';
import type { BIQueryError, BiWidget, DateRange, FilterCondition } from '../../../types/bi';
import type { DrilldownEvent } from '../utils/drilldown';
import { useWidgetQuery } from '../hooks/useWidgetQuery';
import { getWidgetCapabilities } from '../utils/widgetCapabilities';
import BarChart from './charts/BarChart';
import LineChart from './charts/LineChart';
import AreaChart from './charts/AreaChart';
import PieChart from './charts/PieChart';
import DonutChart from './charts/DonutChart';
import KpiCard from './charts/KpiCard';
import DataTable from './charts/DataTable';
import Sparkline from './charts/Sparkline';
import ScatterPlot from './charts/ScatterPlot';
import HeatMap from './charts/HeatMap';
import FunnelChart from './charts/FunnelChart';
import GaugeChart from './charts/GaugeChart';
import TreeMap from './charts/TreeMap';
import WaterfallChart from './charts/WaterfallChart';
import PivotTable from './charts/PivotTable';
import styles from './LiveWidget.module.css';

function isExplainDebugEnabled(): boolean {
  if (import.meta.env.VITE_BI_EXPLAIN_DEBUG === 'true') return true;
  try {
    return new URLSearchParams(window.location.search).get('bi_explain') === '1';
  } catch {
    return false;
  }
}

interface LiveWidgetProps {
  widget: BiWidget;
  dateRange?: DateRange | null;
  globalFilters?: FilterCondition[];
  drilldownFilters?: import('../utils/drilldown').DrilldownFilter[];
  explainDebugEnabled?: boolean;
  onConfigure: () => void;
  onRemove: () => void;
  onRename?: (newTitle: string) => void;
  onDrillDown?: (event: DrilldownEvent) => void;
  onQueryErrorChange?: (widgetId: number, error: BIQueryError | null) => void;
  onCreateAlert?: () => void;
  readOnly?: boolean;
}

export default function LiveWidget({
  widget,
  dateRange,
  globalFilters,
  drilldownFilters,
  explainDebugEnabled,
  onConfigure,
  onRemove,
  onRename,
  onDrillDown,
  onQueryErrorChange,
  onCreateAlert,
  readOnly = false,
}: LiveWidgetProps) {
  const hasMeasures =
    !!widget.data_config &&
    Array.isArray(widget.data_config.measures) &&
    widget.data_config.measures.length > 0;
  const hasDimensions =
    !!widget.data_config &&
    Array.isArray(widget.data_config.dimensions) &&
    widget.data_config.dimensions.length > 0;
  const { requiresDimension, requiresMeasure } = getWidgetCapabilities(widget.widget_type);
  const hasQuery = (!requiresMeasure || hasMeasures) && (!requiresDimension || hasDimensions);
  const explainDebug = explainDebugEnabled ?? isExplainDebugEnabled();

  const { data, isLoading, isError, error } = useWidgetQuery(
    hasQuery ? widget.data_config : null,
    widget.widget_type,
    dateRange,
    widget.projection_config,
    widget.conditional_formatting,
    requiresDimension,
    requiresMeasure,
    globalFilters,
    drilldownFilters,
    explainDebug,
  );

  const title = widget.title || widget.widget_type;

  const [editingTitle, setEditingTitle] = useState(false);
  const [titleDraft, setTitleDraft] = useState(title);
  const [expanded, setExpanded] = useState(false);
  const titleInputRef = useRef<HTMLInputElement>(null);

  // Detect mobile viewport for tap-to-expand behaviour
  const [isMobile, setIsMobile] = useState(false);
  useEffect(() => {
    const mql = window.matchMedia('(max-width: 768px)');
    setIsMobile(mql.matches);
    const handler = (e: MediaQueryListEvent) => setIsMobile(e.matches);
    mql.addEventListener('change', handler);
    return () => mql.removeEventListener('change', handler);
  }, []);

  useEffect(() => {
    if (editingTitle && titleInputRef.current) {
      titleInputRef.current.focus();
      titleInputRef.current.select();
    }
  }, [editingTitle]);

  const startEditTitle = useCallback(() => {
    if (readOnly || !onRename) return;
    setTitleDraft(title);
    setEditingTitle(true);
  }, [readOnly, onRename, title]);

  const saveTitle = useCallback(() => {
    const trimmed = titleDraft.trim();
    if (trimmed && trimmed !== title && onRename) {
      onRename(trimmed);
    }
    setEditingTitle(false);
  }, [titleDraft, title, onRename]);

  const widgetError = isError ? (error as BIQueryError) : null;
  const errorMessage = widgetError?.message || 'Unknown error';
  const errorDetails = Array.isArray(widgetError?.details) ? widgetError.details : [];

  useEffect(() => {
    onQueryErrorChange?.(widget.id, widgetError);
  }, [onQueryErrorChange, widget.id, widgetError]);

  return (
    <>
      <div
        className={styles.widget}
        onClick={isMobile && hasQuery && data ? () => setExpanded(true) : undefined}
      >
        <div className={`${styles.titleBar} widget-drag-handle`}>
          <span className={styles.dragHandle}>
            <FontAwesomeIcon icon={faGripVertical} />
          </span>
          {editingTitle ? (
            <input
              ref={titleInputRef}
              className={styles.titleInput}
              value={titleDraft}
              onChange={(e) => setTitleDraft(e.target.value)}
              onKeyDown={(e) => {
                if (e.key === 'Enter') saveTitle();
                if (e.key === 'Escape') setEditingTitle(false);
              }}
              onBlur={saveTitle}
              onMouseDown={(e) => e.stopPropagation()}
            />
          ) : (
            <span
              className={`${styles.title} ${!readOnly && onRename ? styles.titleEditable : ''}`}
              onDoubleClick={startEditTitle}
            >
              {title}
              {!readOnly && onRename && (
                <FontAwesomeIcon icon={faPen} className={styles.titleEditIcon} />
              )}
            </span>
          )}
          {!readOnly && (
            <div className={styles.actions}>
              {onCreateAlert && hasQuery && (
                <button className={styles.actionBtn} onClick={onCreateAlert} title="Create alert">
                  <FontAwesomeIcon icon={faBell} />
                </button>
              )}
              <button className={styles.actionBtn} onClick={onConfigure} title="Configure">
                <FontAwesomeIcon icon={faGear} />
              </button>
              <button className={styles.actionBtn} onClick={onRemove} title="Remove">
                <FontAwesomeIcon icon={faTimes} />
              </button>
            </div>
          )}
          {isMobile && hasQuery && data && (
            <button className={styles.actionBtn} onClick={() => setExpanded(true)} title="Expand">
              <FontAwesomeIcon icon={faExpand} />
            </button>
          )}
        </div>
        <div className={styles.body}>
          {!hasQuery ? (
            <div className={styles.placeholder}>
              <p>
                Click <FontAwesomeIcon icon={faGear} /> to configure this widget's data source.
              </p>
              {widget.data_config?.dimensions?.length > 0 &&
                requiresMeasure &&
                (!widget.data_config?.measures || widget.data_config.measures.length === 0) && (
                  <p style={{ fontSize: '0.8rem', opacity: 0.7, marginTop: '0.25rem' }}>
                    Add at least one measure to display data.
                  </p>
                )}
            </div>
          ) : isLoading ? (
            <div className={styles.loading}>Loading…</div>
          ) : isError ? (
            <div className={styles.error}>
              <div>Query failed: {errorMessage}</div>
              {errorDetails.length > 0 && (
                <ul style={{ margin: '0.4rem 0 0 1rem', padding: 0 }}>
                  {errorDetails.map((detail, idx) => (
                    <li key={`${detail.code}-${idx}`}>
                      {detail.path ? `${detail.path}: ` : ''}
                      {detail.message}
                    </li>
                  ))}
                </ul>
              )}
            </div>
          ) : data ? (
            <>
              <ChartRenderer widget={widget} data={data} onDrillDown={onDrillDown} />
              {explainDebug && data.explain && (
                <details className={styles.explainBox}>
                  <summary>Explain debug</summary>
                  <div className={styles.explainMeta}>
                    <div>
                      <strong>Grain:</strong> {data.explain.resolved_grain}
                    </div>
                    <div>
                      <strong>Base table:</strong> {data.explain.base_table}
                    </div>
                    <div>
                      <strong>Tables:</strong> {data.explain.involved_tables.join(', ') || 'n/a'}
                    </div>
                    <div>
                      <strong>Entities:</strong>{' '}
                      {data.explain.involved_entities.map((e) => e.key).join(', ') || 'n/a'}
                    </div>
                    <div>
                      <strong>Joins:</strong> {data.explain.joins.length}
                    </div>
                  </div>
                  {data.explain.joins.length > 0 && (
                    <ul className={styles.explainJoins}>
                      {data.explain.joins.map((join, idx) => (
                        <li key={`${idx}-${join}`}>{join}</li>
                      ))}
                    </ul>
                  )}
                </details>
              )}
            </>
          ) : null}
        </div>
      </div>

      {/* Mobile tap-to-expand overlay */}
      {expanded && data && (
        <div className={styles.expandOverlay} onClick={() => setExpanded(false)}>
          <div className={styles.expandedWidget} onClick={(e) => e.stopPropagation()}>
            <div className={styles.expandedHeader}>
              <span className={styles.expandedTitle}>{title}</span>
              <button className={styles.expandedClose} onClick={() => setExpanded(false)}>
                <FontAwesomeIcon icon={faTimes} />
              </button>
            </div>
            <div className={styles.expandedBody}>
              <ChartRenderer widget={widget} data={data} onDrillDown={onDrillDown} />
            </div>
          </div>
        </div>
      )}
    </>
  );
}

/** Maps widget_type to the correct chart component */
function ChartRenderer({
  widget,
  data,
  onDrillDown,
}: {
  widget: BiWidget;
  data: { columns: string[]; rows: Record<string, unknown>[] };
  onDrillDown?: (event: DrilldownEvent) => void;
}) {
  const dims = (widget.data_config.dimensions || []).map((d) => d.replace(/^field_key:/, ''));
  const measures = (widget.data_config.measures || []).map((m) => ({ alias: m.alias }));

  // Resolve date granularity for the first dimension (drives x-axis / pie names)
  const firstDimGranularity =
    (dims[0] && widget.data_config.date_granularity?.[dims[0]]) || undefined;

  switch (widget.widget_type) {
    case 'bar':
      return (
        <BarChart
          data={data}
          dimensions={dims}
          measures={measures}
          style={widget.style_config}
          stacked={!!widget.style_config?.stacked}
          horizontal={!!widget.style_config?.horizontal}
          dateGranularity={firstDimGranularity}
          onDrillDown={onDrillDown}
        />
      );
    case 'line':
      return (
        <LineChart
          data={data}
          dimensions={dims}
          measures={measures}
          style={widget.style_config}
          smooth={!!widget.style_config?.smooth}
          dateGranularity={firstDimGranularity}
          onDrillDown={onDrillDown}
        />
      );
    case 'area':
      return (
        <AreaChart
          data={data}
          dimensions={dims}
          measures={measures}
          style={widget.style_config}
          dateGranularity={firstDimGranularity}
          onDrillDown={onDrillDown}
        />
      );
    case 'pie':
      return (
        <PieChart
          data={data}
          dimensions={dims}
          measures={measures}
          style={widget.style_config}
          dateGranularity={firstDimGranularity}
          onDrillDown={onDrillDown}
        />
      );
    case 'donut':
      return (
        <DonutChart
          data={data}
          dimensions={dims}
          measures={measures}
          style={widget.style_config}
          onDrillDown={onDrillDown}
        />
      );
    case 'kpi': {
      const measureAlias = measures[0]?.alias || data.columns[0];
      const value = data.rows[0]?.[measureAlias];
      const numValue = typeof value === 'number' ? value : Number(value) || 0;
      return (
        <KpiCard
          value={numValue}
          label={widget.title || measureAlias}
          format={(widget.style_config?.format as 'number' | 'currency' | 'percentage') || 'number'}
          prefix={
            typeof widget.style_config?.value_prefix === 'string'
              ? widget.style_config.value_prefix
              : undefined
          }
          postfix={
            typeof widget.style_config?.value_postfix === 'string'
              ? widget.style_config.value_postfix
              : undefined
          }
          unit={
            typeof widget.style_config?.value_unit === 'string'
              ? widget.style_config.value_unit
              : undefined
          }
          thousandsSeparator={
            widget.style_config?.value_thousands_separator === ',' ||
            widget.style_config?.value_thousands_separator === '.' ||
            widget.style_config?.value_thousands_separator === ' ' ||
            widget.style_config?.value_thousands_separator === 'none'
              ? widget.style_config.value_thousands_separator
              : undefined
          }
          decimalSeparator={
            widget.style_config?.value_decimal_separator === '.' ||
            widget.style_config?.value_decimal_separator === ','
              ? widget.style_config.value_decimal_separator
              : undefined
          }
          decimalPlaces={
            typeof widget.style_config?.value_decimal_places === 'number'
              ? widget.style_config.value_decimal_places
              : undefined
          }
          conditionalRules={widget.conditional_formatting}
        />
      );
    }
    case 'sparkline': {
      const mAlias = measures[0]?.alias || data.columns[0];
      const sparkData = data.rows.map((r) => Number(r[mAlias]) || 0);
      return <Sparkline data={sparkData} />;
    }
    case 'data_table':
      return <DataTable data={data} dimensions={dims} onDrillDown={onDrillDown} />;
    case 'scatter':
      return (
        <ScatterPlot
          data={data}
          dimensions={dims}
          measures={measures}
          style={widget.style_config}
          onDrillDown={onDrillDown}
        />
      );
    case 'heatmap':
      return (
        <HeatMap
          data={data}
          dimensions={dims}
          measures={measures}
          style={widget.style_config}
          onDrillDown={onDrillDown}
        />
      );
    case 'funnel':
      return (
        <FunnelChart
          data={data}
          dimensions={dims}
          measures={measures}
          style={widget.style_config}
          onDrillDown={onDrillDown}
        />
      );
    case 'gauge':
      return (
        <GaugeChart
          data={data}
          measures={measures}
          style={widget.style_config}
          min={Number(widget.style_config?.min) || 0}
          max={Number(widget.style_config?.max) || 100}
        />
      );
    case 'treemap':
      return (
        <TreeMap
          data={data}
          dimensions={dims}
          measures={measures}
          style={widget.style_config}
          onDrillDown={onDrillDown}
        />
      );
    case 'waterfall':
      return (
        <WaterfallChart
          data={data}
          dimensions={dims}
          measures={measures}
          style={widget.style_config}
          onDrillDown={onDrillDown}
        />
      );
    case 'pivot_table':
      return (
        <PivotTable data={data} dimensions={dims} measures={measures} style={widget.style_config} />
      );
    default:
      return (
        <div style={{ padding: '1rem', color: 'var(--text-muted)' }}>
          Chart type "{widget.widget_type}" not yet implemented.
        </div>
      );
  }
}
